<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useBranding } from 'shared/composables/useBranding';

import PageHeader from '../SettingsSubPageHeader.vue';
import Icon from 'next/icon/Icon.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { replaceInstallationName } = useBranding();

const globalConfig = useMapGetter('globalConfig/get');

const ALL_CHANNEL_ICONS = [
  'i-woot-line',
  'i-woot-facebook',
  'i-woot-whatsapp',
  'i-woot-instagram',
  'i-woot-messenger',
  'i-woot-website',
  'i-woot-mail',
  'i-woot-sms',
  'i-woot-telegram',
  'i-woot-api',
  'i-woot-twilio',
  'i-woot-gmail',
  'i-woot-outlook',
];

const createFlowSteps = computed(() => {
  const steps = ['CHANNEL', 'INBOX', 'AGENT', 'FINISH'];

  const routes = {
    CHANNEL: 'settings_inbox_new',
    INBOX: 'settings_inboxes_page_channel',
    AGENT: 'settings_inboxes_add_agents',
    FINISH: 'settings_inbox_finish',
  };

  return steps.map(step => {
    return {
      title: t(`INBOX_MGMT.CREATE_FLOW.${step}.TITLE`),
      body: t(`INBOX_MGMT.CREATE_FLOW.${step}.BODY`),
      route: routes[step],
    };
  });
});

const isFirstStep = computed(() => {
  return route.name === 'settings_inbox_new';
});

// True when the user is on a specific channel config page (not the channel picker)
const isOnChannelConfigPage = computed(() => {
  return (
    route.name === 'settings_inboxes_page_channel' ||
    route.name === 'settings_inboxes_add_agents' ||
    route.name === 'settings_inbox_finish'
  );
});

const goBackToChannelSelect = () => {
  router.push({ name: 'settings_inbox_new' });
};

const isFinishStep = computed(() => {
  return route.name === 'settings_inbox_finish';
});

const pageTitle = computed(() => {
  if (isFirstStep.value) {
    return t('INBOX_MGMT.ADD.AUTH.TITLE');
  }
  if (isFinishStep.value) {
    return t('INBOX_MGMT.ADD.AUTH.TITLE_FINISH');
  }
  return t('INBOX_MGMT.ADD.AUTH.TITLE_NEXT');
});

const items = computed(() => {
  return createFlowSteps.value.map(item => ({
    ...item,
    body: replaceInstallationName(item.body),
  }));
});
</script>

<template>
  <div class="inbox-create-flow flex flex-col gap-4 mb-8">
    <PageHeader class="block lg:hidden !mb-0" :header-title="pageTitle" />
    <!-- Channel icons strip – centered horizontally -->
    <div class="hidden lg:flex items-center justify-center gap-2 py-2">
      <div
        v-for="icon in ALL_CHANNEL_ICONS"
        :key="icon"
        class="size-8 bg-slate-100 flex items-center flex-shrink-0 justify-center rounded-full"
      >
        <Icon :icon="icon" class="size-4 text-slate-400" />
      </div>
    </div>
    <!-- Wizard + content, no vertical divider -->
    <div
      class="inbox-create-flow__layout grid grid-cols-1 lg:grid-cols-8 rounded-xl border border-slate-200 min-h-[52rem]"
    >
      <woot-wizard
        class="hidden lg:block col-span-2 h-fit py-8 px-6 border-r border-slate-200"
        :global-config="globalConfig"
        :items="items"
      />
      <div class="col-span-6 overflow-hidden flex flex-col">
        <!-- Back-to-channel-selection bar (only on config sub-pages) -->
        <div v-if="isOnChannelConfigPage" class="channel-config-back-bar">
          <button
            class="channel-config-back-btn"
            @click="goBackToChannelSelect"
          >
            <span class="i-ph-arrow-left-bold size-3.5" />
            Choose a different channel
          </button>
        </div>
        <div class="flex-1 overflow-hidden">
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.inbox-create-flow,
.dark .inbox-create-flow {
  background: #ffffff !important;
}

.inbox-create-flow__layout {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}

.inbox-create-flow :deep(.woot-wizard) {
  background: #ffffff !important;
}

/* Back-to-channel-selection bar */
.channel-config-back-bar {
  padding: 10px 20px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
  flex-shrink: 0;
}

.channel-config-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.channel-config-back-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

/* Force all text tokens to dark on white bg */
.inbox-create-flow :deep(.text-n-slate-12) {
  color: #111827 !important;
}
.inbox-create-flow :deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
.inbox-create-flow :deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
.inbox-create-flow :deep(.text-n-slate-9) {
  color: #9ca3af !important;
}
/* Tailwind slate text */
.inbox-create-flow :deep(.text-slate-700) {
  color: #334155 !important;
}
.inbox-create-flow :deep(.text-slate-600) {
  color: #475569 !important;
}
.inbox-create-flow :deep(.text-slate-500) {
  color: #64748b !important;
}
.inbox-create-flow :deep(.text-slate-400) {
  color: #94a3b8 !important;
}

/* Force bg tokens */
.inbox-create-flow :deep(.bg-n-alpha-2),
.inbox-create-flow :deep([class*='bg-n-alpha']) {
  background: #f3f4f6 !important;
}
.inbox-create-flow :deep(.bg-n-solid-1),
.inbox-create-flow :deep(.bg-n-solid-2),
.inbox-create-flow :deep(.bg-n-background) {
  background: #ffffff !important;
}

/* Force border tokens */
.inbox-create-flow :deep(.border-n-weak),
.inbox-create-flow :deep(.outline-n-weak) {
  border-color: #e5e7eb !important;
}

/* Forms */
.inbox-create-flow :deep(input:not(.reset-base)),
.inbox-create-flow :deep(select:not(.reset-base)),
.inbox-create-flow :deep(textarea:not(.reset-base)),
.inbox-create-flow :deep(.multiselect__tags) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}

/* Labels inside the create flow */
.inbox-create-flow :deep(label) {
  color: #111827 !important;
}

/* General text elements — force dark text on white bg */
.inbox-create-flow :deep(p),
.inbox-create-flow :deep(h1),
.inbox-create-flow :deep(h2),
.inbox-create-flow :deep(h3),
.inbox-create-flow :deep(strong) {
  color: #111827 !important;
}

/* Multiselect dropdown — force light theme */
.inbox-create-flow :deep(.multiselect__content-wrapper) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
}
.inbox-create-flow :deep(.multiselect__option) {
  color: #111827 !important;
  background: #ffffff !important;
}
.inbox-create-flow :deep(.multiselect__option--highlight) {
  background: #dbeafe !important;
  color: #111827 !important;
}
.inbox-create-flow :deep(.multiselect__option--selected) {
  background: #eff6ff !important;
  color: #111827 !important;
}
.inbox-create-flow :deep(.multiselect__option--selected.multiselect__option--highlight) {
  background: #dbeafe !important;
  color: #111827 !important;
}
.inbox-create-flow :deep(.multiselect__option span),
.inbox-create-flow :deep(.multiselect__element span),
.inbox-create-flow :deep(.multiselect__element) {
  color: #111827 !important;
}
.inbox-create-flow :deep(.multiselect__single),
.inbox-create-flow :deep(.multiselect__input) {
  background: #ffffff !important;
  color: #111827 !important;
}
.inbox-create-flow :deep(.multiselect__input::placeholder) {
  color: #9ca3af !important;
}
.inbox-create-flow :deep(.multiselect__tag) {
  background: #dbeafe !important;
  color: #1e40af !important;
}
.inbox-create-flow :deep(.multiselect__tag-icon:after) {
  color: #1e40af !important;
}
.inbox-create-flow :deep(.multiselect__tag-icon:hover) {
  background: #bfdbfe !important;
}

/* ChannelSelector cards inside create flow */
.inbox-create-flow :deep(.channel-selector-card),
.inbox-create-flow :deep([class*='rounded-2xl'][class*='border']) {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}
.inbox-create-flow :deep([class*='rounded-2xl']:hover) {
  border-color: #0066ff !important;
}
</style>
